import axios from 'axios'

// Why this helper exists: when POST /shortlister runs past the API gateway's 30 s limit, the gateway returns
// 503 without CORS headers, so the browser only sees a network error. The API keeps running and saves the
// shortlist anyway, but re-sending the POST would run the whole pipeline a second time. So we wait for the
// save to show up in GET /shortlister instead. Retry stays a user action.

export const SHORTLIST_POLL_INTERVAL_MS = 5000
export const SHORTLIST_RECOVERY_DEADLINE_MS = 75000

// The API Gateway integration timeout is 30 s. A failure with no readable response that arrives sooner is a
// real network error, so it must fail fast instead of polling for 75 s.
export const GATEWAY_TIMEOUT_MIN_ELAPSED_MS = 25000

const GATEWAY_TIMEOUT_STATUSES = [503, 504]

const waitMs = ms => new Promise(resolve => setTimeout(resolve, ms))

// True when the request may still be running on the server: a readable gateway 503/504, a client timeout, or
// a failure with no readable response after the gateway window. Everything else is a real failure, including
// cancels (for example no auth token, so nothing was sent) and no-response errors before the window.
// `elapsedMs` is the time since the request started.
export const isGatewayTimeoutError = (error, elapsedMs) => {
	if (!axios.isAxiosError(error) || axios.isCancel(error)) return false
	if (error.code === 'ECONNABORTED') return true
	if (error.response) return GATEWAY_TIMEOUT_STATUSES.includes(error.response.status)
	return elapsedMs >= GATEWAY_TIMEOUT_MIN_ELAPSED_MS
}

const assertShortlistRows = rows => {
	if (!Array.isArray(rows)) throw new TypeError('GET /shortlister did not return a list')
	return rows
}

// Map of shortlist id to date_created. The API sets date_created to now() on every generate, so a new id or a
// changed value means a save finished. The values are compared as strings and never parsed.
export const toShortlistSnapshot = rows =>
	new Map(assertShortlistRows(rows).map(row => [row.id, row.date_created]))

const findSavedShortlistId = (snapshot, rows) => {
	const saved = rows.find(row => !snapshot.has(row.id) || snapshot.get(row.id) !== row.date_created)
	return saved ? saved.id : null
}

// Resolves { id } once a shortlist is saved after the baseline, or null if none appears by the deadline
// (startedAt + deadlineMs). The caller then surfaces the original POST error. `baseline` is the Map from
// toShortlistSnapshot, taken before the POST. If it is null, the baseline is taken now instead.
export const recoverShortlistAfterTimeout = async ({
	startedAt,
	baseline,
	fetchShortlists,
	sleep = waitMs,
	now = Date.now,
	pollIntervalMs = SHORTLIST_POLL_INTERVAL_MS,
	deadlineMs = SHORTLIST_RECOVERY_DEADLINE_MS,
}) => {
	const deadline = startedAt + deadlineMs
	let snapshot = baseline
	if (!snapshot) {
		try {
			snapshot = toShortlistSnapshot(await fetchShortlists())
		} catch (error) {
			console.warn('Shortlist recovery skipped: could not take a baseline snapshot', error)
			return null
		}
	}

	for (;;) {
		let rows = null
		try {
			rows = assertShortlistRows(await fetchShortlists())
		} catch (error) {
			if (!isGatewayTimeoutError(error, now() - startedAt)) {
				console.warn('Shortlist recovery stopped on a non-network error', error)
				return null
			}
		}
		const savedId = rows && findSavedShortlistId(snapshot, rows)
		if (savedId) return { id: savedId }
		const remainingMs = deadline - now()
		if (remainingMs <= 0) return null
		await sleep(Math.min(pollIntervalMs, remainingMs))
	}
}
